import type { PropsWithChildren } from 'react';
import { createContext, useCallback } from 'react';
import { useContextOrThrow } from '@elfsight/lib-utils';
import type { Price } from '@elfsight/widget-ui';
import { I18nPrice } from '@elfsight/widget-ui';

export const RECAPTCHA_LEGAL_INFO =
  'This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="nofollow noreferrer noopener">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="nofollow noreferrer noopener">Terms of Service</a> apply.';

export const CHECKOUT_PHRASES = [
  'Email for payment confirmation',
  'Change',
  'Order Summary',
  'Back',
  'Your Details',
  'Go To Payment',
  'Payment is already in process',
  'Payment session id is undefined',
  'Unknown payment session status',
  'Payment failed',
  'Payment session was expired or canceled',
  'Something went wrong',
  'Pay Now',
  'Processing Payment...',
  'Secure payments are processed via',
  'Unable to load Stripe',
  'We’re sorry, the payment page isn’t available right now. Please contact us for assistance.',
  'Complete Your Payment',
  'Payment Received',
  RECAPTCHA_LEGAL_INFO
] as const;

export type CheckoutPhrase = (typeof CHECKOUT_PHRASES)[number];
export type CheckoutDictionary = Record<CheckoutPhrase, string>;
export type CheckoutTranslator = (phrase: keyof CheckoutDictionary) => string;

export type PriceComponent = typeof I18nPrice | typeof Price;

export const DEFAULT_CHECKOUT_DICTIONARY: CheckoutDictionary =
  Object.fromEntries(
    CHECKOUT_PHRASES.map((phrase) => [phrase, phrase])
  ) as CheckoutDictionary;

const CheckoutI18nContext = createContext<{
  t: CheckoutTranslator;
  PriceComponent: PriceComponent;
} | null>(null);

type CheckoutI18nProviderProps = PropsWithChildren<{
  dictionary?: CheckoutDictionary;
  PriceComponent?: PriceComponent;
}>;

export function CheckoutI18nProvider({
  children,
  dictionary = DEFAULT_CHECKOUT_DICTIONARY,
  PriceComponent = I18nPrice
}: CheckoutI18nProviderProps) {
  const t = useCallback(
    (phrase: CheckoutPhrase) => dictionary[phrase] ?? phrase,
    [dictionary]
  );

  return (
    <CheckoutI18nContext.Provider value={{ t, PriceComponent }}>
      {children}
    </CheckoutI18nContext.Provider>
  );
}

export function useCheckoutI18n() {
  return useContextOrThrow(CheckoutI18nContext, 'CheckoutI18n');
}
